import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ProgressState, SessionRecord } from '../types';
import {
  appendSession,
  createEmptyProgress,
  parseImportedProgress,
  recordAnswer,
} from '../services/progress';
import { readJson, STORAGE_KEYS, writeJson } from '../services/storage';

interface ProgressContextValue {
  progress: ProgressState;
  /** Record the FIRST attempt on a question or sign. */
  answer: (kind: 'question' | 'sign', id: string, correct: boolean) => void;
  finishSession: (session: SessionRecord) => void;
  resetAll: () => void;
  importFrom: (raw: unknown) => boolean;
}

const ProgressContext = createContext<ProgressContextValue | null>(null);

function loadProgress(): ProgressState {
  const stored = readJson<ProgressState | null>(STORAGE_KEYS.progress, null);
  if (!stored) return createEmptyProgress();
  const parsed = parseImportedProgress(stored);
  return parsed ?? createEmptyProgress();
}

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<ProgressState>(loadProgress);
  const saveTimer = useRef<number | null>(null);

  // Debounced write, so answering quickly does not hammer localStorage.
  useEffect(() => {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      writeJson(STORAGE_KEYS.progress, progress);
    }, 250);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [progress]);

  // Make sure nothing is lost if the tab is closed right after an answer.
  useEffect(() => {
    const flush = () => writeJson(STORAGE_KEYS.progress, progress);
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, [progress]);

  const answer = useCallback((kind: 'question' | 'sign', id: string, correct: boolean) => {
    setProgress((prev) => recordAnswer(prev, kind, id, correct));
  }, []);

  const finishSession = useCallback((session: SessionRecord) => {
    setProgress((prev) => appendSession(prev, session));
  }, []);

  const resetAll = useCallback(() => {
    const fresh = createEmptyProgress();
    setProgress(fresh);
    writeJson(STORAGE_KEYS.progress, fresh);
  }, []);

  const importFrom = useCallback((raw: unknown): boolean => {
    const parsed = parseImportedProgress(raw);
    if (!parsed) return false;
    setProgress(parsed);
    writeJson(STORAGE_KEYS.progress, parsed);
    return true;
  }, []);

  const value = useMemo<ProgressContextValue>(
    () => ({ progress, answer, finishSession, resetAll, importFrom }),
    [progress, answer, finishSession, resetAll, importFrom],
  );

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export function useProgress(): ProgressContextValue {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used inside <ProgressProvider>');
  return ctx;
}
